import { Formik } from "formik";
import { OctagonAlert } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Form,
  Modal,
  Spinner,
  Stack,
} from "react-bootstrap";
import Select from "react-select";

import printService from "../../../../services/print.service";
import shipmentsService from "../../../../services/shipmentService";
import utilsService from "../../../../services/utils.service";

const CreateRackModal = ({ showModal, setShowModal, setCreateRackResult }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [listingAccountOptions, setListingAccountOptions] = useState(null);
  const [shipmentCategoryOptions, setShipmentCategoryOptions] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchListingAccounts = async () => {
      setIsLoading(true);
      const result = await utilsService.getListingAccounts({
        amazonOnly: false,
      });

      if (!isMounted) return;

      if (result.success) {
        const options = [{ label: "None", value: "" }].concat(
          result.listingAccounts.map((e) => {
            return { label: e.listingAccount, value: e.listingAccountId };
          })
        );
        setListingAccountOptions(options);
      } else {
        setError({ message: "Something went wrong. Please try again." });
      }
      setIsLoading(false);
    };

    const fetchShipmentCategories = async () => {
      setIsLoading(true);

      const result = await shipmentsService.getShipmentCategories();

      if (!isMounted) return;

      if (result.success) {
        const options = [{ label: "None", value: "" }].concat(
          result.shipmentCategories.map((e) => {
            return { label: e.shipmentCategory, value: e.shipmentCategoryId };
          })
        );
        setShipmentCategoryOptions(options);
      } else {
        setError({ message: "Something went wrong. Please try again." });
      }

      setIsLoading(false);
    };

    fetchListingAccounts();
    fetchShipmentCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      backdrop="static"
      size="sm"
    >
      <Modal.Header closeButton>
        <Modal.Title>Create New Rack</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="flex-fill alert-blink">
            <div className="alert-icon">
              <OctagonAlert
                className="position-relative top-50 start-50 translate-middle"
                size={20}
              />
            </div>
            <div className="alert-message font-weight-bold">
              {error.message}
            </div>
          </Alert>
        )}
        <Formik
          enableReinitialize
          initialValues={{ listingAccountId: null, shipmentCategoryId: null }}
          onSubmit={async (values) => {
            setError(null);
            setCreateRackResult(null);

            const listingAccountId =
              values.listingAccountId != null && values.listingAccountId !== ""
                ? Number(values.listingAccountId)
                : null;

            const shipmentCategoryId =
              values.shipmentCategoryId != null &&
              values.shipmentCategoryId !== ""
                ? Number(values.shipmentCategoryId)
                : null;

            const result = await shipmentsService.createShipmentRack({
              listingAccountId,
              shipmentCategoryId,
            });

            if (result.success) {
              setCreateRackResult({
                message: "Rack successfully created.",
                newShipmentRack: result.newShipmentRack,
              });

              await printService.printRackLabels([result.newShipmentRack]);

              setTimeout(setShowModal(false), 3000);
            } else {
              setError({ message: result.message });
            }
          }}
        >
          {({
            handleChange,
            handleBlur,
            setFieldTouched,
            handleSubmit,
            handleReset,
            setFieldValue,
            touched,
            dirty,
            isSubmitting,
            isValid,
            errors,
            values,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="m-0">
                  Listing Account (Optional)
                </Form.Label>
                <Select
                  className={`react-select-container${
                    isLoading || isSubmitting
                      ? "-disabled"
                      : touched.listingAccountId && errors.listingAccountId
                      ? "-invalid"
                      : ""
                  } flex-fill`}
                  classNamePrefix="react-select"
                  name="listingAccountId"
                  placeholder="Select a listing account..."
                  onChange={(selectedOption) => {
                    setFieldValue(
                      "listingAccountId",
                      selectedOption?.value ?? null
                    );
                  }}
                  value={
                    listingAccountOptions.find(
                      (option) => option.value === values.listingAccountId
                    ) ?? null
                  }
                  options={listingAccountOptions}
                  isSearchable
                  isDisabled={isSubmitting}
                  isLoading={isLoading}
                  onBlur={() => setFieldTouched("listingAccountId", true)}
                  isInvalid={Boolean(
                    touched.listingAccountId && errors.listingAccountId
                  )}
                />
                {touched.listingAccountId && errors.listingAccountId ? (
                  <div className="invalid-feedback d-block">
                    {errors.listingAccountId}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="m-0">
                  Shipment Category (Optional)
                </Form.Label>
                <Select
                  className={`react-select-container${
                    isLoading || isSubmitting
                      ? "-disabled"
                      : touched.shipmentCategoryId && errors.shipmentCategoryId
                      ? "-invalid"
                      : ""
                  } flex-fill`}
                  classNamePrefix="react-select"
                  name="shipmentCategoryId"
                  placeholder="Select a shipment category..."
                  onChange={(selectedOption) => {
                    setFieldValue(
                      "shipmentCategoryId",
                      selectedOption?.value ?? null
                    );
                  }}
                  value={
                    shipmentCategoryOptions.find(
                      (option) => option.value === values.shipmentCategoryId
                    ) ?? null
                  }
                  options={shipmentCategoryOptions}
                  isSearchable
                  isDisabled={isSubmitting}
                  isLoading={isLoading}
                  onBlur={() => setFieldTouched("shipmentCategoryId", true)}
                  isInvalid={Boolean(
                    touched.shipmentCategoryId && errors.shipmentCategoryId
                  )}
                />
                {touched.shipmentCategoryId && errors.shipmentCategoryId ? (
                  <div className="invalid-feedback d-block">
                    {errors.shipmentCategoryId}
                  </div>
                ) : null}
              </Form.Group>

              <Stack direction="horizontal">
                <Button
                  type="submit"
                  variant="outline-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span>
                      <Spinner
                        className="me-2"
                        animation="border"
                        size="sm"
                        role="status"
                      />
                      Submitting...
                    </span>
                  ) : (
                    "Submit"
                  )}
                </Button>
                <div className="vr ms-3" />
                <Collapse in={dirty} dimension="width">
                  <div>
                    <Button
                      className="ms-3"
                      variant="outline-danger"
                      onClick={handleReset}
                      disabled={isSubmitting || !dirty}
                    >
                      Reset
                    </Button>
                  </div>
                </Collapse>
                <Button
                  className="ms-3"
                  variant="outline-secondary"
                  onClick={() => {
                    handleReset();
                    setShowModal(false);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default CreateRackModal;
